<script setup lang="ts">
/**
 * Modal overlay de traitement (création / paiement / confirmation / success / error).
 * Affiché pendant le submit pour la page adhésion /a/[slug]. Parité totale
 * avec `BslugProcessingOverlay` (billet event) — Karim 2026-06-21.
 */
defineProps<{
  open: boolean;
  step: "creating" | "paying" | "confirming" | "success" | "error";
  progress: number;
  orderError: string | null;
  orderErrorTechnical: boolean;
  contactEmail?: string | null;
}>();

defineEmits<{
  (e: 'dismiss-error'): void;
}>();
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div v-if="open" class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div class="bg-white dark:bg-[#141414] rounded-3xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
          <template v-if="step === 'error'">
            <div class="w-14 h-14 mx-auto mb-5 rounded-2xl flex items-center justify-center bg-red-100 dark:bg-red-900/30">
              <UIcon :name="orderErrorTechnical ? 'i-lucide-server-crash' : 'i-lucide-alert-circle'" class="size-7 text-red-600 dark:text-red-400" />
            </div>
            <h3 class="text-lg font-bold text-[#2d2e5c] dark:text-white mb-2">
              {{ orderErrorTechnical ? $t('adhesion.aslugProcessingOverlay.paiementEchoueTitre') : $t('adhesion.aslugProcessingOverlay.paiementImpossibleTitre') }}
            </h3>
            <p class="text-sm text-[#717198] dark:text-gray-400 break-words">{{ orderError }}</p>
            <p v-if="orderErrorTechnical && contactEmail" class="text-xs text-[#717198] dark:text-gray-500 mt-2">
              <i18n-t keypath="adhesion.aslugProcessingOverlay.contacterSiPersiste" tag="span">
                <template #email>
                  <a :href="`mailto:${contactEmail}`" class="font-semibold underline">{{ contactEmail }}</a>
                </template>
              </i18n-t>
            </p>
            <button
              class="mt-5 w-full py-3 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors focus:outline-none"
              @click="$emit('dismiss-error')"
            >
              {{ $t('commun.actions.fermer') }}
            </button>
          </template>

          <template v-else>
            <div class="w-14 h-14 mx-auto mb-5 rounded-2xl flex items-center justify-center" :class="step === 'success' ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-gray-100 dark:bg-gray-800'">
              <UIcon v-if="step === 'success'" name="i-lucide-check" class="size-7 text-emerald-600 dark:text-emerald-400" />
              <UIcon v-else name="i-lucide-loader-2" class="size-7 text-[#212121] dark:text-white animate-spin" />
            </div>
            <h3 class="text-lg font-bold text-[#2d2e5c] dark:text-white mb-2">
              {{ step === 'creating' ? $t('adhesion.aslugProcessingOverlay.creationAdhesion')
                : step === 'paying' ? $t('adhesion.aslugProcessingOverlay.paiementEnCours')
                : step === 'confirming' ? $t('adhesion.aslugProcessingOverlay.confirmationEnCours')
                : $t('adhesion.aslugProcessingOverlay.adhesionConfirmee') }}
            </h3>
            <p class="text-sm text-[#717198] dark:text-gray-400">
              {{ step === 'success' ? $t('adhesion.aslugProcessingOverlay.redirection') : $t('adhesion.aslugProcessingOverlay.neFermezPas') }}
            </p>
            <div class="mt-5 w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-500 ease-out"
                :class="step === 'success' ? 'bg-emerald-500' : ''"
                :style="{ width: `${progress}%`, ...(step !== 'success' ? { background: 'var(--accent, #212121)' } : {}) }"
              />
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
